import type Phaser from 'phaser';
import { Dir, type Direction } from '../../sim/direction';
import { TILE_SIZE } from '../layout';
import type { MowerId } from '../../content/mowers';
import { drawMowerFrame, mowerFrameSize } from './mowerArt';
import { TILE_FRAME_COUNT, TILE_FRAMES, type TileFrameName } from './tileFrames';

/**
 * Programmatic graybox pixel art, generated into canvas textures at boot. Real art replaces this
 * through the atlas pipeline in Phase 4; frame layouts here define the contract.
 */

export const TILES_KEY = 'tiles';
export const PICKUPS_KEY = 'pickups';
export const CRITTERS_KEY = 'critters';
/** Boss rival: the neighbor's robot mower, one frame per heading (N, E, S, W). */
export const RIVAL_KEY = 'rival';
/** Critter texture frames (drawn facing right; flip for left). */
export const CRITTER_FRAME = {
  squirrel: 0,
  rabbit: 1,
  dog: 2,
  sheep: 3,
  // Woodland wildlife (Rolling Meadows), after the mole.
  deer: 5,
  fox: 6,
  stag: 7,
} as const;
/** A mole peeking out of its molehill (in the critter sheet, after the critters). */
export const MOLE_FRAME = 4;
/** Pickup texture frames, by kind. */
export const PICKUP_FRAME = {
  coin: 0,
  gas: 1,
  repair: 2,
  rare: 3,
  treasure: 0,
  hoard: 4,
  turbo: 5,
  trim: 6,
} as const;

export function mowerTextureKey(mowerId: string): string {
  return `mower-${mowerId}`;
}

/** Mower texture frames: one per heading, then the broken pose. */
export const MOWER_BROKEN_FRAME = 4;

const S = TILE_SIZE;
type Put = (x: number, y: number, color: string) => void;

/** Small deterministic integer hash for texture noise (no Math.random: textures stay stable). */
function noise(x: number, y: number, salt: number): number {
  let h = Math.imul(x * 374761393 + y * 668265263 + salt * 2147483647, 1274126177);
  h = Math.imul(h ^ (h >>> 13), 1103515245);
  return (h ^ (h >>> 16)) >>> 0;
}

function painter(ctx: CanvasRenderingContext2D, ox: number, oy: number): Put {
  return (x, y, color) => {
    if (x < 0 || y < 0 || x >= S || y >= S) return;
    ctx.fillStyle = color;
    ctx.fillRect(ox + x, oy + y, 1, 1);
  };
}

function fill(put: Put, color: string): void {
  for (let y = 0; y < S; y++) for (let x = 0; x < S; x++) put(x, y, color);
}

/** Stamps a small pixel drawing at (x, y); '.' is transparent. */
function stampAt(
  put: Put,
  x: number,
  y: number,
  rows: readonly string[],
  pal: Record<string, string>,
): void {
  rows.forEach((row, dy) => {
    for (let dx = 0; dx < row.length; dx++) {
      const color = pal[row[dx]!];
      if (color) put(x + dx, y + dy, color);
    }
  });
}

/** A raised grass tuft: light tips, mid blades, dark base. */
const TUFT = ['.a..a.', '.a.aA.', 'aAaAAa', 'AbAbAb', 'bbbbbb'];
/** The same tuft with its tips leaning in the breeze (lawn life: swaying grass). */
const TUFT_LEAN = ['..a..a', '.a.aA.', 'aAaAAa', 'AbAbAb', 'bbbbbb'];
const TUFT_PAL = { a: '#d2f58a', A: '#86d957', b: '#2c7a2c' };
/** Where the four tufts sit on a tile; the three grass variants use different layouts. */
const TUFT_LAYOUTS: Record<number, readonly [number, number][]> = {
  1: [
    [0, 0],
    [8, 1],
    [2, 8],
    [9, 9],
  ],
  11: [
    [1, 1],
    [9, 0],
    [0, 9],
    [8, 8],
  ],
  23: [
    [0, 2],
    [7, 0],
    [3, 9],
    [10, 7],
  ],
};

/**
 * Uncut grass: bright, with raised tufts, so the grass still to mow is the liveliest thing on the
 * lawn (docs/ART_DIRECTION.md: mowed is darker and calm).
 */
function paintGrass(put: Put, salt = 1, lean = false): void {
  fill(put, '#3f9a35');
  const tuft = lean ? TUFT_LEAN : TUFT;
  for (const [x, y] of TUFT_LAYOUTS[salt] ?? TUFT_LAYOUTS[1]!) stampAt(put, x, y, tuft, TUFT_PAL);
}

/** A stamp with its top `rows` rows (the tips) nudged one pixel right: leaning in the breeze. */
function leaning(stamp: readonly string[], rows: number): string[] {
  return stamp.map((row, i) => (i < rows ? `.${row.slice(0, -1)}` : row));
}

/**
 * Wildflower meadow grass: the lawn's tufts with daisies, buttercups and clover dotted through
 * them (a patch of it left half mowed grows back, so it must look unlike ordinary grass).
 */
function paintMeadowGrass(put: Put, lean = false): void {
  // A warmer, lighter green than the lawn, its own tufts, and wildflowers all through it.
  fill(put, '#58ad42');
  const tuft = lean ? TUFT_LEAN : TUFT;
  for (const [x, y] of TUFT_LAYOUTS[11] ?? TUFT_LAYOUTS[1]!) stampAt(put, x, y, tuft, TUFT_PAL);
  const flowers: [number, number, string, string][] = [
    [2, 3, '#ffffff', '#ffe14a'],
    [11, 2, '#ffe14a', '#d99a0c'],
    [6, 7, '#d9a8ff', '#a066e8'],
    [14, 6, '#ffffff', '#ffe14a'],
    [13, 11, '#ff9ac0', '#e05a8a'],
    [3, 12, '#ffe14a', '#d99a0c'],
    [8, 14, '#ffffff', '#ffe14a'],
    [9, 10, '#ffe14a', '#d99a0c'],
    [1, 8, '#d9a8ff', '#a066e8'],
  ];
  for (const [x, y, petal, heart] of flowers) {
    const dx = lean ? 1 : 0;
    put(x + dx, y - 1, petal);
    put(x - 1 + dx, y, petal);
    put(x + 1 + dx, y, petal);
    put(x + dx, y, heart);
    put(x, y + 1, '#2c7a2c');
  }
}

/** Every frame that sways in the breeze, and how to paint it upright or leaning. */
const SWAYING = {
  grass: (put: Put, lean: boolean) => paintGrass(put, 1, lean),
  grass2: (put: Put, lean: boolean) => paintGrass(put, 11, lean),
  grass3: (put: Put, lean: boolean) => paintGrass(put, 23, lean),
  tallGrass: (put: Put, lean: boolean) => paintTallGrass(put, lean),
  tallGrassCut: (put: Put, lean: boolean) => paintTallGrassCut(put, lean),
  wheat: (put: Put, lean: boolean) => paintWheat(put, lean),
  meadowGrass: (put: Put, lean: boolean) => paintMeadowGrass(put, lean),
} as const satisfies Partial<Record<TileFrameName, (put: Put, lean: boolean) => void>>;

export type SwayingFrame = keyof typeof SWAYING;
export const SWAYING_FRAMES = Object.keys(SWAYING) as SwayingFrame[];

/**
 * Lawn life: repaints the grass frames (every length, and wheat) upright or leaning. Every such
 * tile on the lawn draws from these frames, so one cheap repaint sways the whole lawn. No-op when
 * the tiles come from a PNG override (not a canvas).
 */
export function swayGrass(scene: Phaser.Scene, lean: (frame: SwayingFrame) => boolean): void {
  // A canvas texture (generated art) has a 2D context; a PNG override does not.
  const tex = scene.textures.get(TILES_KEY) as Partial<Phaser.Textures.CanvasTexture>;
  if (!tex.context || !tex.refresh) return;
  for (const name of SWAYING_FRAMES) {
    const i = TILE_FRAMES.indexOf(name);
    SWAYING[name](painter(tex.context, i * S, 0), lean(name));
  }
  tex.refresh.call(tex);
}

/**
 * The flat, calm ground under obstacles (rocks, trees, gnomes...). Not tufted, so an obstacle
 * never looks like grass still to mow.
 */
function paintLawnBase(put: Put): void {
  paintPropBase(put);
}

/**
 * The lawn under a standing prop: the grass's own green, smooth (no tufts: nothing to mow), so the
 * prop stands on the lawn rather than in a dark square.
 */
function paintPropBase(put: Put): void {
  fill(put, '#3a9433');
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      const n = noise(x, y, 13) % 29;
      if (n === 0) put(x, y, '#4aa53e');
      else if (n === 1) put(x, y, '#2f8530');
    }
  }
}

const MOWED_BASE: Record<Direction, { base: string; line: string }> = {
  [Dir.N]: { base: '#27702e', line: '#2c7834' },
  [Dir.S]: { base: '#1d5a25', line: '#215f29' },
  [Dir.E]: { base: '#256c2c', line: '#2a7431' },
  [Dir.W]: { base: '#1f5e27', line: '#23642b' },
};

function paintMowed(put: Put, dir: Direction): void {
  const { base, line } = MOWED_BASE[dir];
  fill(put, base);
  const vertical = dir === Dir.N || dir === Dir.S;
  for (let i = 2; i < S; i += 5) {
    for (let j = 0; j < S; j++) {
      if (vertical) put(i, j, line);
      else put(j, i, line);
    }
  }
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) if (noise(x, y, 3) % 41 === 0) put(x, y, '#3a8a3e');
  }
}

/**
 * Lawn Art's show lawn: short, perfect grass cut in a fine diamond pattern, a calm mid green that
 * is neither picture tone (the light and dark stripes), so the picture stands out on it.
 */
function paintShowLawn(put: Put): void {
  fill(put, '#2e7a35');
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      const d1 = (x + y) % 8;
      const d2 = (x - y + 16) % 8;
      if (d1 === 0 || d2 === 0) put(x, y, '#34843b');
      else if (noise(x, y, 7) % 37 === 0) put(x, y, '#2a7230');
    }
  }
}

/**
 * Lawn Art's picture tones, far apart so the picture reads at a glance: light, a bright fresh
 * green in stripes running up and down; dark, a deep green in stripes running across.
 */
function paintArtTone(put: Put, light: boolean): void {
  fill(put, light ? '#5cbd52' : '#174d1f');
  for (let i = 1; i < S; i += 4) {
    for (let j = 0; j < S; j++) {
      if (light) put(i, j, '#6fcd63');
      else put(j, i, '#123f19');
    }
  }
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      if (noise(x, y, 5) % 43 === 0) put(x, y, light ? '#8ad97c' : '#215c29');
    }
  }
}

function paintGround(put: Put): void {
  // A sunny gravel path: warm sand with pebbles, each lit on top and shaded beneath.
  fill(put, '#c39456');
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) if (noise(x, y, 2) % 13 === 0) put(x, y, '#b3844a');
  }
  for (let k = 0; k < 9; k++) {
    const x = noise(k, 1, 61) % (S - 2);
    const y = 1 + (noise(k, 2, 61) % (S - 3));
    put(x, y, '#f2d79c');
    put(x + 1, y, noise(k, 3, 61) % 2 ? '#f2d79c' : '#e0bd80');
    put(x, y + 1, '#8c5f33');
    put(x + 1, y + 1, '#8c5f33');
  }
}

function paintRock(put: Put): void {
  paintLawnBase(put);
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      const ex = (x - 7.5) / 5.5;
      const ey = (y - 8.5) / 4.8;
      const d = ex * ex + ey * ey;
      if (d > 1) continue;
      if (d > 0.72) put(x, y, '#4d5156');
      else if (ex + ey < -0.6) put(x, y, '#b9bcc0');
      else put(x, y, '#8a8d91');
    }
  }
}

/** Calls `paint` for every pixel inside an ellipse, with normalized distance d (0 center, 1 edge). */
function ellipse(
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  paint: (x: number, y: number, d: number, ex: number, ey: number) => void,
): void {
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      const ex = (x - cx) / rx;
      const ey = (y - cy) / ry;
      const d = ex * ex + ey * ey;
      if (d <= 1) paint(x, y, d, ex, ey);
    }
  }
}

function shadow(put: Put, cx: number, cy: number, rx: number, ry: number): void {
  ellipse(cx, cy, rx, ry, (x, y) => put(x, y, '#163f19'));
}

function paintDirt(put: Put): void {
  fill(put, '#8d6a43');
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      const n = noise(x, y, 4) % 11;
      if (n === 0) put(x, y, '#735433');
      else if (n === 1) put(x, y, '#a3825a');
    }
  }
}

// Key tiles also differ from grass in lightness, not only hue, so they read with any color vision
// (checked by tools/cvd_check.py).
/**
 * Long grass's base: a light, thick meadow (lighter than the lawn, never mistaken for a mowed
 * stripe), dense short blades all over. Its long blades stand up off the tile on their own layer
 * (bladesArt.ts), into the tile behind.
 */
function paintTallGrass(put: Put, lean = false): void {
  fill(put, '#5aae46');
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      const n = noise(x + (lean && y % 4 === 0 ? 1 : 0), y, 81) % 7;
      if (n === 0) put(x, y, '#8fd062');
      else if (n === 1) put(x, y, '#c4ea86');
      else if (n === 2) put(x, y, '#3f8a33');
    }
  }
}

/** Tall grass after one weak pass: the same light meadow, flattened, clippings lying about. */
function paintTallGrassCut(put: Put, lean = false): void {
  fill(put, '#6cba52');
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      const n = noise(x + (lean && y % 5 === 0 ? 1 : 0), y, 82) % 7;
      if (n === 0) put(x, y, '#a6dc72');
      else if (n === 1) put(x, y, '#dfeca0');
      else if (n === 2) put(x, y, '#4f9a3c');
    }
  }
  for (const [x, y] of [
    [5, 1],
    [12, 2],
    [2, 7],
    [14, 8],
    [8, 14],
    [10, 5],
  ] as const)
    put(x, y, '#dfeca0');
}

function paintSticks(put: Put): void {
  paintLawnBase(put);
  const sticks: [number, number, number][] = [
    [2, 4, 9],
    [5, 10, 8],
    [9, 3, 5],
  ];
  for (const [x0, y0, len] of sticks) {
    for (let i = 0; i < len; i++) {
      put(x0 + i, y0 + Math.floor(i / 2), '#e3c08c');
      put(x0 + i, y0 + Math.floor(i / 2) + 1, '#5a3a1e');
    }
  }
}

/** A flower bed after the mower: churned soil, flattened stems, scattered petals. */
function paintFlowerBedTrampled(put: Put): void {
  fill(put, '#4a3220');
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      const n = noise(x, y, 31) % 13;
      if (n === 0)
        put(x, y, '#6b4a2b'); // clods
      else if (n === 1) put(x, y, '#2e1f14'); // ruts
    }
  }
  // Flattened stems, all bent the same way (run over).
  for (const [x, y] of [
    [1, 3],
    [6, 2],
    [10, 6],
    [3, 9],
    [8, 11],
    [12, 12],
  ] as const) {
    for (let i = 0; i < 4; i++) put(x + i, y + (i >> 1), '#3f6b2a');
  }
  // Petals knocked off and scattered.
  const petals = ['#e0533d', '#f2c94c', '#b06fd8', '#f4f1de'];
  for (let i = 0; i < 9; i++) {
    put(noise(i, 1, 41) % S, noise(i, 2, 41) % S, petals[i % petals.length]!);
  }
}

function paintFlowerBed(put: Put): void {
  // Rich dark soil, bold blooms each ringed in ink so they read at a glance.
  fill(put, '#6b4429');
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      const n = noise(x, y, 6) % 9;
      if (n === 0) put(x, y, '#4f311c');
      else if (n === 1) put(x, y, '#83573a');
    }
  }
  const petals = ['#ff6fa8', '#ffe14a', '#c084ff', '#fffaf0', '#ff5b47'];
  const blooms: [number, number][] = [
    [3, 3],
    [11, 3],
    [7, 8],
    [2, 12],
    [12, 12],
  ];
  blooms.forEach(([cx, cy], i) => {
    put(cx - 1, cy + 2, '#4ea83f');
    put(cx + 1, cy + 2, '#4ea83f');
    for (const [ox, oy] of [
      [0, -2],
      [-1, -1],
      [1, -1],
      [-2, 0],
      [2, 0],
      [-1, 1],
      [1, 1],
      [0, 2],
    ] as const)
      put(cx + ox, cy + oy, '#10180c');
    const c = petals[(i + noise(cx, cy, 6)) % petals.length]!;
    for (const [ox, oy] of [
      [0, -1],
      [-1, 0],
      [1, 0],
      [0, 1],
    ] as const)
      put(cx + ox, cy + oy, c);
    put(cx, cy, '#ffd23f');
  });
}

function paintMud(put: Put): void {
  fill(put, '#5c4128');
  ellipse(6, 6, 4, 3, (x, y) => put(x, y, '#4a3320'));
  ellipse(11, 11, 3, 2, (x, y) => put(x, y, '#4a3320'));
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) if (noise(x, y, 7) % 19 === 0) put(x, y, '#7d5d3d');
  }
  put(5, 5, '#8fa3b0');
  put(10, 10, '#8fa3b0');
}

/**
 * Water: ripple lines drifting sideways with `phase` (0..15). They wrap around the tile, so a pond
 * of water tiles reads as one surface flowing gently along; alternate rows drift the other way.
 */
function paintWater(put: Put, phase = 0): void {
  fill(put, '#1d63b8');
  for (let x = 0; x < S; x++) put(x, 0, '#4ea9ef');
  for (let y = 2; y < S; y += 5) {
    const drift = ((y / 5) | 0) % 2 === 0 ? phase : -phase;
    const off = (y * 3) % 7;
    const at = (x: number) => (((x + drift) % S) + S) % S;
    for (let x = off; x < off + 4; x++) put(at(x), y, '#a2e0ff');
    for (let x = off + 8; x < off + 11; x++) put(at(x), Math.min(S - 1, y + 2), '#a2e0ff');
  }
  // A glint that comes and goes.
  if (phase % 8 < 2) put((phase * 5 + 3) % S, 9, '#ffffff');
}

/** Lawn life: ripples the water frame (every water tile draws from it). No-op for PNG art. */
export function rippleWater(scene: Phaser.Scene, phase: number): void {
  const tex = scene.textures.get(TILES_KEY) as Partial<Phaser.Textures.CanvasTexture>;
  if (!tex.context || !tex.refresh) return;
  paintWater(painter(tex.context, TILE_FRAMES.indexOf('water') * S, 0), phase % S);
  tex.refresh.call(tex);
}

function paintBirdBath(put: Put): void {
  paintLawnBase(put);
  shadow(put, 8, 13, 5, 2);
  for (let y = 7; y <= 13; y++) for (let x = 7; x <= 8; x++) put(x, y, '#9aa0a6');
  for (let x = 5; x <= 10; x++) put(x, 13, '#7d8287');
  ellipse(7.5, 5, 6, 3, (x, y, d) => put(x, y, d > 0.55 ? '#c9ccd0' : '#5aa9e6'));
}

/** A bird bath after the mower: the bowl in stone chunks, a puddle where the water went. */
function paintBirdBathSmashed(put: Put): void {
  paintLawnBase(put);
  // A small splash, not a pond: kept well apart from the water tile.
  ellipse(8.5, 9.5, 3, 1.6, (x, y, d) => {
    if (d < 0.9) put(x, y, '#5aa9e6');
  });
  // The pedestal, snapped off.
  for (let x = 7; x <= 9; x++) put(x, 8, '#9aa0a6');
  const chunks: [number, number, string][] = [
    [3, 4, '#c9ccd0'],
    [4, 4, '#9aa0a6'],
    [11, 3, '#c9ccd0'],
    [12, 3, '#c9ccd0'],
    [12, 4, '#7d8287'],
    [2, 12, '#9aa0a6'],
    [13, 12, '#c9ccd0'],
    [7, 13, '#7d8287'],
    [8, 13, '#9aa0a6'],
    [6, 6, '#c9ccd0'],
  ];
  for (const [x, y, c] of chunks) put(x, y, c);
}

function paintTree(put: Put): void {
  paintLawnBase(put);
  shadow(put, 9, 10, 7, 5);
  ellipse(7.5, 7, 7.2, 6.8, (x, y, d, ex, ey) => {
    if (d > 0.82) put(x, y, '#1f4f1d');
    else if (ex + ey < -0.5 && noise(x, y, 8) % 3 !== 0) put(x, y, '#4f9e45');
    else put(x, y, noise(x, y, 9) % 5 === 0 ? '#2a5f27' : '#357a31');
  });
}

function paintShrub(put: Put): void {
  paintLawnBase(put);
  shadow(put, 8, 12, 6, 3);
  ellipse(7.5, 8, 6, 5.5, (x, y, d, ex, ey) => {
    if (d > 0.78) put(x, y, '#143611');
    else if (ex + ey < -0.4) put(x, y, '#8fcf6a');
    else put(x, y, noise(x, y, 10) % 4 === 0 ? '#2f6d28' : '#3f8a35');
  });
  put(5, 7, '#e0533d');
  put(9, 10, '#e0533d');
}

function paintGnome(put: Put): void {
  paintLawnBase(put);
  shadow(put, 8, 14, 4, 1.5);
  for (let y = 1; y <= 6; y++) {
    const half = Math.floor((y + 1) / 2);
    for (let x = 8 - half; x <= 7 + half; x++) put(x, y, '#d8432f');
  }
  for (let x = 5; x <= 10; x++) put(x, 7, '#f2c9a0');
  for (let y = 8; y <= 10; y++) for (let x = 5; x <= 10; x++) put(x, y, '#f4f1de');
  for (let y = 11; y <= 14; y++) for (let x = 5; x <= 10; x++) put(x, y, '#3a7bd5');
  put(6, 7, '#222326');
  put(9, 7, '#222326');
}

/** What is left of a gnome after the mower: hat, beard and coat in shards on the lawn. */
function paintGnomeSmashed(put: Put): void {
  paintLawnBase(put);
  const shards: [number, number, string][] = [
    [3, 4, '#d8432f'],
    [4, 4, '#d8432f'],
    [4, 5, '#a83222'],
    [11, 3, '#d8432f'],
    [12, 4, '#a83222'],
    [7, 8, '#f4f1de'],
    [8, 8, '#f4f1de'],
    [8, 9, '#d7d2bd'],
    [2, 11, '#3a7bd5'],
    [3, 11, '#3a7bd5'],
    [3, 12, '#2a5fa8'],
    [12, 11, '#3a7bd5'],
    [13, 12, '#2a5fa8'],
    [10, 13, '#f2c9a0'],
    [6, 13, '#f4f1de'],
  ];
  for (const [x, y, c] of shards) put(x, y, c);
  // The base still standing, snapped off.
  for (let x = 6; x <= 9; x++) put(x, 11, '#2a5fa8');
  for (let x = 6; x <= 9; x++) put(x, 12, '#3a7bd5');
}

/** A wooden dog house: red roof, plank walls, a dark round doorway facing down onto the lawn. */
function paintDogHouse(put: Put): void {
  paintLawnBase(put);
  shadow(put, 8, 14, 6, 1.5);
  // Walls.
  for (let y = 6; y <= 14; y++)
    for (let x = 3; x <= 12; x++) put(x, y, y % 3 === 0 ? '#9c6a3c' : '#b98249');
  // Roof.
  for (let y = 1; y <= 6; y++) {
    const half = y + 2;
    for (let x = 8 - half; x <= 7 + half; x++)
      if (x >= 1 && x <= 14) put(x, y, y === 6 ? '#8f2f22' : '#c8553d');
  }
  // Doorway.
  for (let y = 9; y <= 14; y++) for (let x = 6; x <= 9; x++) put(x, y, '#2a1c12');
  put(6, 9, '#b98249');
  put(9, 9, '#b98249');
}

/**
 * A pop-up sprinkler head in three-quarter view: a dark collar in the lawn, a steel riser lit on
 * its left, a safety-orange cap and a nozzle on top. Built to be noticed.
 */
function paintSprinkler(put: Put): void {
  paintLawnBase(put);
  shadow(put, 9, 13, 5, 1.5);
  ellipse(7.5, 12, 4.6, 2, (x, y, d) => put(x, y, d > 0.6 ? '#1f2023' : '#3a3b3f'));
  for (let y = 7; y <= 11; y++) {
    for (let x = 5; x <= 10; x++) put(x, y, x <= 6 ? '#e2e6ea' : x >= 9 ? '#6e757c' : '#a8afb6');
  }
  ellipse(7.5, 6.5, 3.6, 1.8, (x, y, _d, ex, ey) =>
    put(x, y, ex + ey < -0.3 ? '#ffc070' : ey > 0.3 ? '#c4580f' : '#ff8a1f'),
  );
  for (let y = 2; y <= 5; y++) put(7, y, y === 2 ? '#ffffff' : '#c9ced3');
  put(8, 4, '#8f969d');
  put(8, 5, '#8f969d');
}

/** A broken sprinkler: its riser snapped off at the collar, the cap tossed aside, a puddle. */
function paintSprinklerBroken(put: Put): void {
  paintLawnBase(put);
  ellipse(8, 12, 5.5, 2.4, (x, y, d) => put(x, y, d > 0.7 ? '#3a86c8' : '#7cc4f0'));
  ellipse(7.5, 10.5, 4, 1.8, (x, y, d) => put(x, y, d > 0.6 ? '#1f2023' : '#3a3b3f'));
  for (let x = 6; x <= 9; x++) put(x, 9, x <= 6 ? '#e2e6ea' : '#a8afb6');
  ellipse(12.5, 5, 2.2, 1.3, (x, y, _d, _ex, ey) => put(x, y, ey < 0 ? '#ff8a1f' : '#c4580f'));
  for (const [x, y] of [
    [4, 6],
    [10, 8],
    [5, 13],
  ] as const)
    put(x, y, '#e8f6ff');
}

/** A sprinkler turned off with the key: screwed down flush, just its collar and closed cap. */
function paintSprinklerOff(put: Put): void {
  paintLawnBase(put);
  ellipse(7.5, 10, 4, 2, (x, y, d) => put(x, y, d > 0.6 ? '#1f2023' : '#3a3b3f'));
  ellipse(7.5, 9.5, 2.6, 1.3, (x, y, _d, ex) => put(x, y, ex < -0.2 ? '#a8afb6' : '#6e757c'));
}

/** The judges' table in three-quarter view: a white cloth over it, folds in its skirt, a rosette. */
function paintJudgesTable(put: Put): void {
  paintGround(put);
  ellipse(9, 14, 7, 1.6, (x, y) => put(x, y, '#8a6a3e'));
  // The tabletop, lit along its back edge, then the cloth hanging down the front.
  for (let y = 3; y <= 6; y++)
    for (let x = 1; x <= 14; x++) put(x, y, y === 3 ? '#ffffff' : '#f4f1de');
  for (let y = 7; y <= 13; y++) {
    for (let x = 1; x <= 14; x++)
      put(x, y, x % 4 === 2 ? '#c9c4b0' : y === 13 ? '#b8b29c' : '#e6e1cc');
  }
  for (let x = 1; x <= 14; x++) put(x, 7, '#d6d0ba');
  // The rosette pinned to the front, its ribbons hanging below.
  ellipse(8, 9, 2.4, 2.4, (x, y, d) => put(x, y, d > 0.55 ? '#d8432f' : '#ffd23f'));
  put(7, 9, '#fff6b8');
  put(7, 12, '#d8432f');
  put(9, 12, '#d8432f');
  put(7, 13, '#a83222');
  put(9, 13, '#a83222');
  // Two scorecards on top.
  for (const x of [3, 11]) {
    put(x, 4, '#6fa0ff');
    put(x + 1, 4, '#6fa0ff');
    put(x, 5, '#3a63d0');
    put(x + 1, 5, '#3a63d0');
  }
}

/** The sheep pen's floor: golden straw (the fence is drawn round the whole pen). */
function paintPen(put: Put): void {
  fill(put, '#d9b860');
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      const n = noise(x, y, 6) % 9;
      if (n === 0) put(x, y, '#b8943c');
      else if (n === 1) put(x, y, '#f0d68a');
    }
  }
  // A few strands of straw lying about.
  for (const [x, y] of [
    [3, 4],
    [10, 9],
    [6, 12],
  ] as const) {
    put(x, y, '#f0d68a');
    put(x + 1, y, '#f0d68a');
    put(x + 2, y + 1, '#b8943c');
  }
}

/** The pen gate: trodden ground with hoof prints leading in. */
function paintPenGate(put: Put): void {
  paintGround(put);
  for (const [x, y] of [
    [6, 2],
    [9, 5],
    [6, 8],
    [9, 11],
    [6, 14],
  ] as const) {
    put(x, y, '#7a5a32');
    put(x + 1, y, '#7a5a32');
  }
}

/** The robot mower's dock: a gray charging pad with a yellow bolt. */
function paintRoboDock(put: Put): void {
  paintLawnBase(put);
  for (let y = 2; y <= 13; y++)
    for (let x = 2; x <= 13; x++) put(x, y, y === 13 ? '#5d6470' : '#8a93a2');
  for (let x = 2; x <= 13; x++) put(x, 2, '#aab3c2');
  const bolt = ['...##', '..##.', '.####', '..##.', '.##..', '.#...'];
  bolt.forEach((row, y) =>
    [...row].forEach((ch, x) => {
      if (ch === '#') put(x + 5, y + 5, '#ffd23f');
    }),
  );
}

/**
 * The putting green: smooth, short and clearly lighter than the lawn (it reads by lightness, not
 * only hue), with a faint diamond pattern from its own fine mowing.
 */
function paintGreen(put: Put): void {
  fill(put, '#7fcf68');
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      if ((x + y) % 8 === 0 || (x - y + 16) % 8 === 0) put(x, y, '#8fdb78');
      else if (noise(x, y, 13) % 29 === 0) put(x, y, '#72c25c');
    }
  }
}

/** The green after a mower drove over it: wheel ruts torn into the turf. */
function paintGreenTrampled(put: Put): void {
  paintGreen(put);
  for (let y = 0; y < S; y++) {
    for (const x of [3, 4, 11, 12]) put(x, y, noise(x, y, 5) % 3 === 0 ? '#6b4a2b' : '#4a3220');
  }
}

/** The flag pin in its hole, on the green. */
function paintFlag(put: Put): void {
  paintGreen(put);
  ellipse(7.5, 13, 2.2, 1.2, (x, y) => put(x, y, '#1b2a16'));
  for (let y = 2; y <= 13; y++) put(7, y, '#f4f1de');
  for (let y = 2; y <= 6; y++) for (let x = 8; x <= 8 + (6 - y); x++) put(x, y, '#e0533d');
  for (let y = 2; y <= 6; y++) put(8, y, '#b83a28');
}

/** A ball washer by the tee: a post with a blue box and a crank. */
function paintBallWasher(put: Put): void {
  paintLawnBase(put);
  shadow(put, 9, 14, 4, 1.5);
  for (let y = 8; y <= 14; y++) {
    put(7, y, '#3b3e44');
    put(8, y, '#5d6470');
  }
  for (let y = 2; y <= 8; y++)
    for (let x = 4; x <= 11; x++) put(x, y, y === 2 ? '#7cb0e8' : '#3a7bd5');
  for (let x = 4; x <= 11; x++) put(x, 8, '#2a5aa0');
  for (let x = 12; x <= 13; x++) put(x, 4, '#c9c4b0');
  put(13, 5, '#c9c4b0');
}

/**
 * A big tree's trunk in three-quarter view (its crown, from the crowns sheet, spreads over it): a
 * thick bole rising off the top of the tile, deep bark grooves, roots flaring into the lawn, lit
 * from the left, a shadow to the right.
 */
function paintBigTree(put: Put): void {
  paintLawnBase(put);
  for (let y = 4; y < 15; y++)
    for (let x = 11; x < 15; x++) if (y > 6 || x < 13) put(x, y, '#163f19');
  const bark = ['#e0ad6c', '#c4874a', '#9a6232', '#6a3f1f', '#3b230f'] as const;
  const art = [
    '....01122223....',
    '....01122233....',
    '....011224233...',
    '....01122423....',
    '....01422223....',
    '....01422233....',
    '....01122233....',
    '....01122423....',
    '....01122423....',
    '...0011122233...',
    '..001112222333..',
    '.001.0112232.33.',
    '.11..0112223..3.',
    '.....01.22.3....',
    '.....1...2......',
  ];
  art.forEach((row, y) =>
    [...row].forEach((ch, x) => {
      if (ch !== '.') put(x, y, bark[Number(ch)]!);
    }),
  );
}

/** A rounded headstone, seen from the front and a little above, its shadow behind it. */
function paintHeadstone(put: Put): void {
  paintLawnBase(put);
  shadow(put, 9, 4, 5, 2.5);
  for (let y = 3; y <= 13; y++) {
    for (let x = 4; x <= 11; x++) {
      // Rounded top.
      const cx = x - 7.5;
      if (y < 6 && cx * cx + (y - 6) * (y - 6) * 1.6 > 14) continue;
      const edge = x === 4 || x === 11;
      put(x, y, edge ? '#6f747b' : x <= 6 ? '#c7cbd1' : '#a7adb4');
    }
  }
  // An engraved line or two, and the turf at its foot.
  for (let x = 6; x <= 9; x++) put(x, 7, '#7d838b');
  for (let x = 6; x <= 9; x++) put(x, 9, '#7d838b');
  for (let x = 4; x <= 11; x++) put(x, 14, '#163f19');
}

/** An old iron lantern on a post: a warm glow behind the glass. */
function paintLantern(put: Put): void {
  paintLawnBase(put);
  shadow(put, 9, 14, 3.5, 1.5);
  for (let y = 7; y <= 13; y++) {
    put(7, y, '#1f2023');
    put(8, y, '#3a3d42');
  }
  for (let x = 6; x <= 9; x++) put(x, 14, '#1f2023');
  // The lamp: a cap, glass on a frame, a base.
  for (let x = 6; x <= 9; x++) put(x, 1, '#1f2023');
  for (let x = 5; x <= 10; x++) put(x, 2, '#1f2023');
  for (let y = 3; y <= 6; y++) {
    put(5, y, '#1f2023');
    put(10, y, '#1f2023');
    for (let x = 6; x <= 9; x++) put(x, y, y === 3 ? '#fff2a8' : '#f2c94c');
  }
  for (let x = 5; x <= 10; x++) put(x, 7, '#1f2023');
}

/**
 * A thorn patch: bramble canes arching over bare earth in three-quarter view, each lit along its
 * top and shaded beneath, thorns pricking out, dark leaves and purple thistle heads. Canes wrap
 * round the tile's sides, so a patch of them reads as one tangle. Darker than grass (not only
 * another hue), so it reads with any color vision.
 */
function paintThorns(put: Put): void {
  fill(put, '#3b2a1e');
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      const n = noise(x, y, 41) % 11;
      if (n === 0) put(x, y, '#2b1d14');
      else if (n === 1) put(x, y, '#4e3826');
    }
  }
  const wrap = (v: number) => ((v % S) + S) % S;
  // Arches, back to front: start x, span, height, foot row.
  const arches: [number, number, number, number][] = [
    [-3, 10, 5, 6],
    [6, 11, 6, 7],
    [12, 8, 4, 5],
    [1, 9, 6, 12],
    [9, 10, 6, 13],
    [-4, 8, 4, 17],
    [5, 9, 5, 18],
  ];
  arches.forEach(([x0, span, h, foot], k) => {
    let last = -1;
    for (let i = 0; i <= span * 2; i++) {
      const t = i / (span * 2);
      const x = wrap(Math.round(x0 + span * t));
      const y = Math.round(foot - Math.sin(Math.PI * t) * h);
      if (x === last && i % 2 === 1) continue;
      last = x;
      if (y + 1 >= 0 && y + 1 < S) put(x, wrap(y + 1), '#22100f');
      put(x, wrap(y), '#5e2638');
      if (y - 1 >= 0) put(x, y - 1, t > 0.3 && t < 0.7 ? '#a8566a' : '#7a3348');
      // A thorn on the outside of the arch now and then.
      if ((i + k) % 5 === 2 && y - 2 >= 0) put(x, y - 2, '#f2e2c4');
    }
  });
  // Bramble leaves, dark and glossy.
  for (const [x, y] of [
    [3, 3],
    [13, 9],
    [7, 14],
    [10, 2],
    [1, 10],
  ] as const) {
    put(x, y, '#2f5a2a');
    put(x + 1, y, '#4f8a3a');
    put(x, y + 1, '#1e3d1c');
  }
  // Thistle heads: a green cup with a purple tuft, lit on top.
  for (const [x, y] of [
    [4, 6],
    [12, 4],
    [10, 11],
  ] as const) {
    put(x, y + 1, '#4f7a36');
    put(x + 1, y + 1, '#2f5a2a');
    put(x - 1, y, '#9a4ec2');
    put(x, y, '#c77ae0');
    put(x + 1, y, '#9a4ec2');
    put(x, y - 1, '#ecb6ff');
    put(x + 1, y - 1, '#c77ae0');
  }
}

/** Weathered dead wood: lit, light, mid, dark, deep (dead trees and the logs they leave). */
const DEAD_WOOD = ['#d6c6aa', '#ad9a82', '#857260', '#5e4f42', '#3a3029'] as const;

/**
 * A dead tree standing (the catalog frame; on the lawn the props sheet draws it, taller): a
 * gnarled gray trunk forking into bare crooked limbs, lit from the left.
 */
function paintDeadTree(put: Put): void {
  paintLawnBase(put);
  shadow(put, 9, 14, 5, 1.5);
  const art = [
    '..1........1....',
    '..12......12..1.',
    '...2..1...2..12.',
    '...12.12.12.12..',
    '.1..1212.2212...',
    '.12..12222.2....',
    '..1221222.......',
    '....01223.......',
    '.....0123.......',
    '.....01233......',
    '.....01423......',
    '.....01443......',
    '.....01223......',
    '....001223......',
    '...0011223.33...',
    '..00.0122..33...',
  ];
  art.forEach((row, y) =>
    [...row].forEach((ch, x) => {
      if (ch !== '.') put(x, y, DEAD_WOOD[Number(ch)]!);
    }),
  );
}

/**
 * A fallen log lying across a tile (east-west; `along` turns it north-south), weathered gray like
 * the dead tree it was: a lit top, bark streaks down its length, its near side in shade, a shadow
 * on the lawn. Seamless along its length, so a run of tiles reads as one trunk.
 */
function paintLog(put: Put, along = false, from = 0, to = S): void {
  if (from === 0 && to === S) paintLawnBase(put);
  const [lit, light, mid, dark, deep] = DEAD_WOOD;
  for (let i = from; i < to; i++) {
    // Across the log: 5..12 is wood (top to near side), 13..14 its shadow.
    for (let j = 5; j <= 14; j++) {
      let c: string;
      if (j >= 13) c = '#163f19';
      else if (j === 5) c = light;
      else if (j === 6) c = lit;
      else if (j <= 8) c = (i * 7 + j) % 9 === 0 ? mid : light;
      else if (j <= 10) c = (i * 5 + j * 3) % 7 === 0 ? deep : mid;
      else c = j === 12 ? deep : dark;
      if (along) put(j - 2, i, c);
      else put(i, j, c);
    }
  }
}

/**
 * The fallen tree's top, where it came down: the trunk coming in from one side (the stump's side)
 * and forking into bare crooked limbs that lie spread on the lawn. `toward` is the way it fell.
 */
function paintLogTip(put: Put, toward: Direction = Dir.E): void {
  paintLawnBase(put);
  const [lit, light, mid, dark] = DEAD_WOOD;
  // Drawn for a fall to the east, then turned: (x, y) along and across the fall.
  const across = toward === Dir.N || toward === Dir.S;
  const back = toward === Dir.W || toward === Dir.N;
  const at = (a: number, b: number): [number, number] => {
    const along = back ? S - 1 - a : a;
    return across ? [b - 2, along] : [along, b];
  };
  // The trunk's last stretch, tapering.
  if (across) paintLog(put, true, back ? 10 : 0, back ? S : 6);
  else paintLog(put, false, back ? 10 : 0, back ? S : 6);
  // Limbs: polylines from the trunk's end, thick near it, a pixel wide at their tips.
  const limbs: [number, number][][] = [
    [
      [5, 9],
      [8, 8],
      [10, 5],
      [12, 4],
      [14, 2],
    ],
    [
      [10, 5],
      [11, 7],
      [13, 7],
    ],
    [
      [5, 10],
      [8, 11],
      [11, 13],
      [14, 13],
    ],
    [
      [8, 11],
      [9, 14],
    ],
    [
      [6, 9],
      [10, 9],
      [12, 10],
      [15, 9],
    ],
    [
      [12, 4],
      [12, 1],
    ],
  ];
  limbs.forEach((pts, k) => {
    for (let p = 0; p + 1 < pts.length; p++) {
      const [x0, y0] = pts[p]!;
      const [x1, y1] = pts[p + 1]!;
      const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0));
      const thick = k < 3 && p === 0 ? 2 : 1;
      for (let i = 0; i <= n; i++) {
        const a = Math.round(x0 + ((x1 - x0) * i) / n);
        const b = Math.round(y0 + ((y1 - y0) * i) / n);
        // Lit on the side toward the sun, shaded on the other; a shadow beneath on the lawn.
        const [sx, sy] = at(a, b + thick);
        put(sx, sy, '#163f19');
        for (let t = 0; t < thick; t++) {
          const [x, y] = at(a, b + t);
          put(x, y, t === 0 && thick > 1 ? lit : p === 0 ? light : mid);
        }
        if (i === n && p === pts.length - 2) {
          const [x, y] = at(a, b);
          put(x, y, dark);
        }
      }
    }
  });
}

/** A cobblestone walk: rounded gray setts in staggered rows, mortar between. */
function paintCobble(put: Put): void {
  fill(put, '#8f8a80');
  for (let row = 0; row < 4; row++) {
    const y0 = row * 4;
    const shift = row % 2 === 0 ? 0 : 2;
    for (let col = -1; col < 4; col++) {
      const x0 = col * 4 + shift;
      const tone = noise(col + 7, row + 3, 51) % 3;
      const c = tone === 0 ? '#cdc7b9' : tone === 1 ? '#c0baad' : '#d9d3c5';
      for (let y = y0; y < y0 + 3; y++) {
        for (let x = x0; x < x0 + 3; x++) put(x, y, c);
      }
      put(x0, y0, '#ece6d8');
    }
  }
}

/**
 * A wooden footbridge's deck from above: planks laid across the way over (`along`: the deck runs
 * north to south). The rails are drawn by the lawn view, only where the deck meets the water.
 */
function paintBridge(put: Put, along = false): void {
  const at = (i: number, j: number, c: string) => (along ? put(j, i, c) : put(i, j, c));
  for (let i = 0; i < S; i++) {
    for (let j = 0; j < S; j++) {
      // Planks run across the direction of travel, with gaps every 4.
      const plank = i % 4 === 3 ? '#7a5634' : noise(i, j, 61) % 9 === 0 ? '#b98a5a' : '#a77a4a';
      at(i, j, plank);
    }
  }
}

/** A stone urn spilling with flowers, from above. */
function paintUrn(put: Put): void {
  paintLawnBase(put);
  shadow(put, 9, 10, 5, 4);
  ellipse(7.5, 8, 5.2, 5, (x, y, d) => put(x, y, d > 0.7 ? '#8c877d' : '#c4bfb2'));
  ellipse(7.5, 7.5, 3.6, 3.4, (x, y) =>
    put(x, y, noise(x, y, 63) % 3 === 0 ? '#e8708a' : noise(x, y, 64) % 2 ? '#3f8a35' : '#f2c94c'),
  );
}

/** An urn the mower knocked over: stone shards, spilled soil and petals. */
function paintUrnSmashed(put: Put): void {
  paintLawnBase(put);
  ellipse(8, 9, 4, 2.6, (x, y) => put(x, y, '#6b4a2b'));
  for (const [x, y, c] of [
    [3, 4, '#c4bfb2'],
    [4, 4, '#8c877d'],
    [12, 5, '#c4bfb2'],
    [11, 12, '#8c877d'],
    [4, 12, '#c4bfb2'],
    [7, 8, '#e8708a'],
    [9, 10, '#f2c94c'],
    [6, 10, '#e8708a'],
  ] as const)
    put(x, y, c);
}

/**
 * A thick line of `w` pixels lying on the lawn from (x0, y0) to (x1, y1): lit along its top edge,
 * dark along its bottom, a shadow beneath (broken planks, poles, a knocked-over post).
 */
function lying(
  put: Put,
  [x0, y0, x1, y1]: readonly [number, number, number, number],
  [lit, body, dark]: readonly [string, string, string],
  w = 2,
): void {
  const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0));
  for (let i = 0; i <= n; i++) {
    const x = Math.round(x0 + ((x1 - x0) * i) / n);
    const y = Math.round(y0 + ((y1 - y0) * i) / n);
    put(x + 1, y + w, '#163f19');
    for (let k = 0; k < w; k++) put(x, y + k, k === 0 ? lit : k === w - 1 ? dark : body);
  }
}

/** A bench a mower drove through: splintered planks lying askew, a snapped leg, splinters. */
function paintBenchSmashed(put: Put): void {
  paintLawnBase(put);
  const wood = ['#e0ad6c', '#c4874a', '#6a3f1f'] as const;
  lying(put, [1, 4, 9, 2], wood);
  lying(put, [5, 8, 14, 10], wood);
  lying(put, [2, 12, 8, 13], wood);
  // A leg, snapped (dark iron), and splinters flung about.
  lying(put, [11, 3, 13, 6], ['#555a75', '#2a2c40', '#2a2c40'], 1);
  for (const [x, y] of [
    [13, 13],
    [3, 7],
    [10, 13],
    [14, 6],
  ] as const)
    put(x, y, '#e0ad6c');
}

/** A bush a heavy mower ran over: flattened into a low mat of leaves, a tire track through it. */
function paintShrubFlattened(put: Put): void {
  paintLawnBase(put);
  ellipse(8, 9, 6.6, 4.2, (x, y, d, ex, ey) => {
    if (d > 0.85 && noise(x, y, 33) % 2 === 0) return;
    put(x, y, ex + ey < -0.5 ? '#4ea83f' : noise(x, y, 34) % 4 === 0 ? '#123b1e' : '#2f8236');
  });
  for (let y = 5; y <= 13; y++) {
    put(6, y, '#1f5f2b');
    put(9, y, '#1f5f2b');
  }
  // Twigs snapped, leaves knocked loose.
  for (const [x, y, c] of [
    [2, 4, '#6a3f1f'],
    [3, 3, '#6a3f1f'],
    [13, 4, '#86cf52'],
    [14, 12, '#4ea83f'],
    [1, 12, '#86cf52'],
    [12, 14, '#6a3f1f'],
  ] as const)
    put(x, y, c);
}

/** A potted topiary knocked over: the pot in shards, soil spilled, its clipped ball rolled off. */
function paintTopiarySmashed(put: Put): void {
  paintLawnBase(put);
  ellipse(6, 11, 4, 2, (x, y) => put(x, y, '#6b4a2b'));
  for (const [x, y, c] of [
    [2, 9, '#ff5b47'],
    [3, 9, '#c8303a'],
    [8, 13, '#ff5b47'],
    [9, 13, '#c8303a'],
    [4, 14, '#c8303a'],
    [1, 12, '#ffa684'],
  ] as const)
    put(x, y, c);
  // The ball, rolled away and squashed a little.
  shadow(put, 11.5, 7.5, 3.6, 1.6);
  ellipse(11, 5.5, 3.4, 2.8, (x, y, _d, ex, ey) =>
    put(x, y, ex + ey < -0.5 ? '#86cf52' : ex + ey > 0.6 ? '#2f8236' : '#4ea83f'),
  );
}

/** A trash bin knocked over on its side, its lid rolled off, a bit of paper spilled. */
function paintBinTipped(put: Put): void {
  paintLawnBase(put);
  // The bin lying east-west: blue, lit along its top, its open end showing.
  for (let x = 3; x <= 12; x++) {
    put(x, 11, '#163f19');
    put(x, 6, '#6f9bf0');
    put(x, 7, '#4a74dc');
    put(x, 8, x % 3 === 0 ? '#2c4fb0' : '#3a63d0');
    put(x, 9, '#3a63d0');
    put(x, 10, '#263f8f');
  }
  for (let y = 6; y <= 10; y++) put(13, y, y === 6 || y === 10 ? '#263f8f' : '#1a2350');
  // The yellow lid, off on the lawn, and spilled paper.
  ellipse(6, 3, 2.6, 1.2, (x, y, _d, _ex, ey) => put(x, y, ey < 0 ? '#ffe066' : '#c99a17'));
  for (const [x, y] of [
    [14, 12],
    [15, 9],
    [13, 13],
  ] as const)
    put(x, y, '#fffaf0');
}

/** The golf flag pin knocked down: lying across the green, its red flag beside the hole. */
function paintFlagDown(put: Put): void {
  paintGreen(put);
  ellipse(7.5, 13, 2.2, 1.2, (x, y) => put(x, y, '#1b2a16'));
  lying(put, [2, 10, 12, 6], ['#ffffff', '#f4f1de', '#c9c4b0'], 1);
  for (const [x, y] of [
    [11, 4],
    [12, 4],
    [13, 4],
    [12, 3],
    [13, 3],
    [13, 5],
  ] as const)
    put(x, y, '#e0533d');
  put(14, 4, '#b83a28');
}

/** A corner flag knocked flat: its pole along the lawn, the pennant crumpled at the end. */
function paintCornerFlagDown(put: Put): void {
  paintLawnBase(put);
  lying(put, [2, 11, 11, 8], ['#ffffff', '#f4f1de', '#c9c4b0'], 1);
  for (const [x, y, c] of [
    [11, 6, '#ff8a1f'],
    [12, 6, '#ff8a1f'],
    [12, 7, '#ffd23f'],
    [13, 7, '#ffd23f'],
    [11, 7, '#ff8a1f'],
  ] as const)
    put(x, y, c);
  put(2, 12, '#3a3b3f'); // the snapped-off foot
}

/** A ball washer the Fairway Mower ran down: its post snapped, the blue box on its side. */
function paintBallWasherDown(put: Put): void {
  paintLawnBase(put);
  // The stump of the post.
  for (const [x, y] of [
    [7, 12],
    [8, 12],
  ] as const)
    put(x, y, '#3b3e44');
  lying(put, [1, 10, 6, 7], ['#8a90a8', '#5d6470', '#3b3e44']);
  // The box, lying on its side: lit top, a darker side, the crank sticking up.
  for (let y = 4; y <= 9; y++) {
    for (let x = 8; x <= 14; x++) put(x, y, y === 4 ? '#7cb0e8' : y >= 8 ? '#2a5aa0' : '#3a7bd5');
  }
  for (let x = 8; x <= 14; x++) put(x, 10, '#163f19');
  put(15, 6, '#c9c4b0');
  put(15, 5, '#c9c4b0');
  put(10, 6, '#7cb0e8');
}

/** A beehive smashed: its stand knocked over, the straw dome in pieces, honey spilled. */
function paintBeehiveSmashed(put: Put): void {
  paintLawnBase(put);
  ellipse(8, 11, 4, 1.8, (x, y) => put(x, y, '#d99a0c'));
  ellipse(7.5, 10.5, 2.4, 1, (x, y) => put(x, y, '#ffd23f'));
  for (const [x, y, c] of [
    [2, 4, '#f6df8a'],
    [3, 4, '#b8902e'],
    [12, 3, '#f6df8a'],
    [13, 4, '#e2bf55'],
    [11, 13, '#e2bf55'],
    [4, 13, '#f6df8a'],
  ] as const)
    put(x, y, c);
  lying(put, [2, 7, 6, 6], ['#e0ad6c', '#c4874a', '#6a3f1f'], 1);
}

/** A marble statue on its plinth: a square base and a round figure, lit from the top left. */
function paintStatue(put: Put): void {
  paintLawnBase(put);
  shadow(put, 9, 11, 6, 4);
  for (let y = 3; y <= 13; y++) {
    for (let x = 3; x <= 12; x++) {
      const edge = x === 3 || x === 12 || y === 3 || y === 13;
      put(x, y, edge ? '#8e8a84' : x + y < 14 ? '#e4e0d8' : '#c9c4ba');
    }
  }
  ellipse(7.5, 7.5, 3.2, 3.2, (x, y, d, ex, ey) =>
    put(x, y, d > 0.7 ? '#9d9890' : ex + ey < -0.3 ? '#ffffff' : '#ece8e0'),
  );
}

/**
 * A round fountain in three-quarter view: a stone basin (its lit rim, its front face in shade),
 * rippling water, and a spout splashing up from a pedestal in the middle.
 */
function paintFountain(put: Put): void {
  paintLawnBase(put);
  shadow(put, 9, 14, 7, 1.6);
  // The basin's front face, under the rim.
  ellipse(7.5, 10.5, 7.4, 4.5, (x, y, _d, ex) =>
    put(x, y, ex < -0.5 ? '#b6b0a3' : ex > 0.5 ? '#6f6a62' : '#8f8a80'),
  );
  // The rim, then the water inside it.
  ellipse(7.5, 7.5, 7.4, 4.4, (x, y, _d, ex, ey) =>
    put(x, y, ex + ey < -0.4 ? '#f0ece2' : '#cfc9bb'),
  );
  ellipse(7.5, 7.8, 5.8, 3.2, (x, y, _d, ex, ey) =>
    put(
      x,
      y,
      ey < -0.5
        ? '#2a6fb0'
        : noise(x, y, 67) % 6 === 0
          ? '#a2e0ff'
          : ex < -0.4
            ? '#4ea9ef'
            : '#3a86c8',
    ),
  );
  // The pedestal and its spout.
  for (let y = 5; y <= 8; y++) {
    put(7, y, '#e8e4da');
    put(8, y, '#a9a395');
  }
  for (const [x, y, c] of [
    [7, 1, '#ffffff'],
    [8, 1, '#d9f4ff'],
    [7, 2, '#d9f4ff'],
    [8, 2, '#ffffff'],
    [7, 3, '#a2e0ff'],
    [8, 3, '#d9f4ff'],
    [6, 2, '#a2e0ff'],
    [9, 2, '#a2e0ff'],
    [5, 4, '#d9f4ff'],
    [10, 4, '#d9f4ff'],
    [5, 7, '#ffffff'],
    [10, 8, '#ffffff'],
  ] as const)
    put(x, y, c);
}

/** A clipped topiary ball in the lawn: neat, bright, a trim line round it. */
function paintTopiary(put: Put): void {
  paintLawnBase(put);
  shadow(put, 9, 10, 6, 5);
  ellipse(7.5, 7.5, 6, 6, (x, y, d, ex, ey) => {
    if (d > 0.82) put(x, y, '#143611');
    else if (ex + ey < -0.6) put(x, y, '#8fcf6a');
    else put(x, y, noise(x, y, 69) % 5 === 0 ? '#2f6d28' : '#4a9a3e');
  });
}

/** The maze's hedge: a solid wall of clipped leaves, lit along its top. */
function paintMazeHedge(put: Put): void {
  fill(put, '#1a4523');
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      const n = noise(x, y, 71) % 7;
      if (n === 0) put(x, y, '#2f6d28');
      else if (n === 1) put(x, y, '#163f19');
    }
  }
  for (let x = 0; x < S; x++) {
    put(x, 0, '#4f9e45');
    if (noise(x, 1, 72) % 2 === 0) put(x, 1, '#3f8a35');
  }
}

/**
 * Under a goal: a worn goalmouth with the chalk of the goal line along its middle (the lawn view draws
 * the goal itself standing over it, posts, crossbar and net, in three-quarter view).
 */
function paintGoal(put: Put): void {
  // A goalmouth's worn turf: darker, scuffed to earth here and there.
  fill(put, '#2a6e2a');
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      const n = noise(x, y, 91) % 9;
      if (n === 0) put(x, y, '#6b5a36');
      else if (n === 1) put(x, y, '#3a8233');
    }
  }
  for (let x = 0; x < S; x++) put(x, 8, '#e8f0dc');
}

/** A corner flag: a thin pole and a bright pennant. */
function paintCornerFlag(put: Put): void {
  paintLawnBase(put);
  shadow(put, 9, 14, 3, 1.2);
  for (let y = 2; y <= 14; y++) put(7, y, '#f4f1de');
  for (let y = 2; y <= 7; y++) {
    for (let x = 8; x <= 8 + (7 - y) + 1 && x < 14; x++) put(x, y, y < 5 ? '#ff8a1f' : '#ffd23f');
  }
}

/** A training cone, from above: orange rings rising to a white band and tip. */
function paintCone(put: Put): void {
  paintLawnBase(put);
  shadow(put, 9, 10, 5, 4);
  ellipse(7.5, 8, 5, 5, (x, y, d) =>
    put(x, y, d > 0.7 ? '#b4621a' : d > 0.35 ? '#ff8a1f' : d > 0.15 ? '#ffffff' : '#ff8a1f'),
  );
}

/** A cone the mower knocked flying: lying on its side, scuffed turf. */
function paintConeSmashed(put: Put): void {
  paintLawnBase(put);
  for (let i = 0; i < 8; i++) {
    put(4 + i, 7 + Math.floor(i / 3), i < 2 ? '#ffffff' : '#ff8a1f');
    put(4 + i, 8 + Math.floor(i / 3), '#b4621a');
  }
}

/** What the saw (or a falling tree) leaves: a stump in three-quarter view, rings on its cut top. */
function paintTreeStump(put: Put): void {
  paintDirt(put);
  ellipse(9, 12.5, 5.5, 2, (x, y) => put(x, y, '#4a3220'));
  // Its bark side: lit left, dark right, roots at the foot.
  for (let y = 8; y <= 12; y++) {
    for (let x = 3; x <= 12; x++)
      put(
        x,
        y,
        x <= 4 ? '#c4874a' : x >= 11 ? '#6a3f1f' : (x + y) % 4 === 0 ? '#6a3f1f' : '#9a6232',
      );
  }
  for (const [x, y] of [
    [2, 12],
    [13, 12],
    [7, 13],
  ] as const) {
    put(x, y, '#6a3f1f');
    put(x + 1, y, '#9a6232');
  }
  // The cut top: pale wood with rings.
  ellipse(7.5, 7.5, 5, 2.6, (x, y, d) =>
    put(x, y, d > 0.8 ? '#6a3f1f' : d > 0.45 ? '#f2d79c' : d > 0.25 ? '#c49a62' : '#f2d79c'),
  );
  put(7, 7, '#c49a62');
  for (const [x, y] of [
    [2, 3],
    [13, 4],
    [12, 14],
  ] as const)
    put(x, y, '#e2c48f');
}

/** A molehill: a mound of fresh dark earth pushed up through mowed lawn. */
function paintMolehill(put: Put): void {
  paintMowed(put, Dir.N);
  ellipse(8, 10, 5.5, 3.6, (x, y) => put(x, y, '#163f19')); // shadow
  ellipse(7.5, 8.5, 5, 4.2, (x, y, d, ex, ey) => {
    if (d > 0.8) put(x, y, '#4a3220');
    else if (ex + ey < -0.5) put(x, y, '#8d6a43');
    else put(x, y, noise(x, y, 9) % 5 === 0 ? '#4a3220' : '#6b4a2b');
  });
  // Clods thrown out round it.
  for (const [x, y] of [
    [2, 12],
    [13, 4],
    [12, 13],
  ] as const)
    put(x, y, '#6b4a2b');
}

/** The back deck's planks (the 'house' tile; the lawn draws the whole deck over its block). */
function paintHouse(put: Put): void {
  fill(put, '#b07a45');
  for (let y = 0; y < S; y += 4) {
    for (let x = 0; x < S; x++) put(x, y + 3, '#8a5a30');
    for (let x = 0; x < S; x++) put(x, y + 1, '#d39a5e');
    const off = (y / 4) % 2 === 0 ? 3 : 11;
    for (let yy = y; yy < y + 3; yy++) put(off, yy, '#8a5a30');
  }
}

// ── Neighborhood Park ───────────────────────────────────────────────────────────────────────

function paintSand(put: Put): void {
  fill(put, '#e2c98f');
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      const n = noise(x, y, 12) % 9;
      if (n === 0) put(x, y, '#c9ad70');
      else if (n === 1) put(x, y, '#f2e0b0');
    }
  }
}

function paintBench(put: Put): void {
  paintLawnBase(put);
  shadow(put, 8, 12, 7, 2);
  for (const x of [2, 13]) for (let y = 9; y <= 12; y++) put(x, y, '#3b3e44');
  for (const y of [4, 7, 10]) {
    for (let x = 1; x <= 14; x++) {
      put(x, y, '#e3c08c');
      put(x, y + 1, '#8a5a2e');
    }
  }
}

function paintLampPost(put: Put): void {
  paintLawnBase(put);
  shadow(put, 9, 14, 4, 1.5);
  for (let y = 4; y <= 13; y++) {
    put(7, y, '#2b2f36');
    put(8, y, '#4a4f58');
  }
  for (let x = 5; x <= 10; x++) put(x, 14, '#2b2f36');
  ellipse(7.5, 2.5, 3.2, 2.4, (x, y, d) => put(x, y, d > 0.6 ? '#2b2f36' : '#fbe9a0'));
}

/** A blue wheelie bin with a yellow lid (owner, 2026-10-28: the gray one read as a headstone). */
function paintBin(put: Put): void {
  paintLawnBase(put);
  shadow(put, 9, 13, 5, 2);
  for (let y = 4; y <= 13; y++) {
    for (let x = 4; x <= 11; x++) {
      put(x, y, x === 4 ? '#6f9bf0' : x === 11 ? '#263f8f' : '#3a63d0');
    }
    if (y > 5 && y < 13) for (const x of [6, 9]) put(x, y, '#2c4fb0');
  }
  for (let x = 5; x <= 10; x++) put(x, 5, '#1d2f6e');
  for (let x = 3; x <= 12; x++) {
    put(x, 1, '#ffe066');
    put(x, 2, '#ffd23f');
    put(x, 3, '#c99a17');
  }
  for (const x of [4, 11]) put(x, 14, '#222326');
}

// ── Farm ────────────────────────────────────────────────────────────────────────────────────

function paintBarn(put: Put): void {
  fill(put, '#a83a2c');
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x += 4) put(x, y, '#7e2a20');
  }
  for (let x = 0; x < S; x++) {
    put(x, 0, '#f4f1de');
    put(x, 15, '#f4f1de');
  }
}

/** A farm rail fence across the tile, in three-quarter view: a post, two rails, a shadow. */
function paintFence(put: Put): void {
  paintLawnBase(put);
  for (let x = 0; x < S; x++) {
    put(x, 13, '#2f8530');
    for (const y of [6, 10]) {
      put(x, y, '#f2d79c');
      put(x, y + 1, '#c4874a');
      put(x, y + 2, '#6a3f1f');
    }
  }
  for (let y = 3; y <= 13; y++) {
    put(7, y, '#e0ad6c');
    put(8, y, '#9a6232');
  }
  put(7, 3, '#f2d79c');
  put(8, 3, '#c4874a');
}

function paintWheat(put: Put, lean = false): void {
  // Like tall grass, in gold: dark stalk gaps and pale heads keep it apart from grass by
  // lightness as well as hue (tools/cvd_check.py).
  fill(put, '#4a3810');
  const upright = ['y..y..', 'g.yg.y', 'g.gg.g', 'gGgGgg', 'GbGbGG', 'bbGbbb', 'bbbbbb'];
  const stalks = lean ? leaning(upright, 2) : upright;
  const pal = { y: '#fff3c0', g: '#e8c45a', G: '#b8902e', b: '#4a3810' };
  for (const [x, y] of [
    [0, 0],
    [7, 1],
    [3, 8],
    [10, 9],
    [0, 9],
  ] as const)
    stampAt(put, x, y, stalks, pal);
}

function paintHayBale(put: Put): void {
  paintLawnBase(put);
  shadow(put, 9, 10, 7, 6);
  ellipse(7.5, 7.5, 6.8, 6.8, (x, y, d) => {
    if (d > 0.85) put(x, y, '#7d6226');
    else if (d > 0.5 && d < 0.62) put(x, y, '#b8943c');
    else if (d > 0.18 && d < 0.27) put(x, y, '#b8943c');
    else put(x, y, '#e4c56c');
  });
}

function paintCrops(put: Put): void {
  fill(put, '#5a3c22');
  for (let y = 0; y < S; y++) if (y % 4 === 3) for (let x = 0; x < S; x++) put(x, y, '#46301b');
  for (let y = 1; y < S; y += 4) {
    for (let x = 2; x < S; x += 4) {
      put(x, y, '#9bd77f');
      put(x - 1, y, '#5fa543');
      put(x + 1, y, '#5fa543');
      put(x, y + 1, '#5fa543');
      put(x, y - 1, '#c4ee9f');
    }
  }
}

/**
 * A hedgerow: a solid wall of clipped leaves. Seamless, so a row of them reads as one hedge; the
 * lawn view adds its lit top, dark front face and shadow where the hedge ends.
 */
function paintHedge(put: Put): void {
  fill(put, '#2f8236');
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      const n = noise(x, y, 14) % 7;
      if (n === 0) put(x, y, '#86cf52');
      else if (n === 1) put(x, y, '#4ea83f');
      else if (n === 2) put(x, y, '#1f5f2b');
    }
  }
}

function paintTrough(put: Put): void {
  paintLawnBase(put);
  shadow(put, 9, 12, 7, 3);
  for (let y = 4; y <= 11; y++) {
    for (let x = 1; x <= 14; x++) {
      const edge = y === 4 || y === 11 || x === 1 || x === 14;
      put(x, y, edge ? '#4d5156' : y === 5 ? '#b8def8' : '#5a9fd6');
    }
  }
}

// ── Garden hose ─────────────────────────────────────────────────────────────────────────────

/** Hose connection bits: which sides of the tile the hose leaves through. */
export const HOSE_BIT = { N: 1, E: 2, S: 4, W: 8 } as const;

/**
 * A garden hose segment on calm lawn: a blue tube with a dark outline (owner, 2026-10-28: the lime
 * one was too close to the grass; it reads as "don't touch" by lightness, not just hue), running
 * from the tile's middle out through the sides in `mask`. One side only is the hose's free end,
 * with a brass nozzle on it (the lawn view sprays water from it); none is a coiled end.
 */
function paintHose(put: Put, mask: number): void {
  paintLawnBase(put);
  const ink = '#101a33';
  const tube = '#3a8fe0';
  const shine = '#a8d8ff';
  // A thin tube, 4 px wide (pixels 6..9) with its outline: a garden hose, not a pipe. Arms reach
  // the tile edge.
  const lo = 6;
  const hi = 9;
  const isTube = (x: number, y: number) =>
    x >= 0 &&
    y >= 0 &&
    x < S &&
    y < S &&
    ((x >= lo && x <= hi && y >= lo && y <= hi) ||
      (!!(mask & HOSE_BIT.N) && x >= lo && x <= hi && y < lo) ||
      (!!(mask & HOSE_BIT.S) && x >= lo && x <= hi && y > hi) ||
      (!!(mask & HOSE_BIT.W) && y >= lo && y <= hi && x < lo) ||
      (!!(mask & HOSE_BIT.E) && y >= lo && y <= hi && x > hi));
  // An inside pixel touches tube on every side (the tile edge counts as tube, so arms run off it).
  const inside = (x: number, y: number) =>
    [
      [x - 1, y],
      [x + 1, y],
      [x, y - 1],
      [x, y + 1],
    ].every(([nx, ny]) => nx! < 0 || ny! < 0 || nx! >= S || ny! >= S || isTube(nx!, ny!));
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      if (!isTube(x, y)) continue;
      put(x, y, inside(x, y) ? tube : ink);
    }
  }
  // A shine along the tube, a pixel in from its upper/left edge.
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      if (!isTube(x, y) || !inside(x, y)) continue;
      const horizontal = y === lo + 1 && !!(mask & (HOSE_BIT.E | HOSE_BIT.W));
      const vertical = x === lo + 1 && !!(mask & (HOSE_BIT.N | HOSE_BIT.S));
      if (horizontal || vertical) put(x, y, shine);
    }
  }
  if (mask === 0) put(lo + 1, lo + 1, shine);
  // The free end's brass nozzle, on the side away from the one link.
  const out =
    mask === HOSE_BIT.W
      ? 'E'
      : mask === HOSE_BIT.E
        ? 'W'
        : mask === HOSE_BIT.N
          ? 'S'
          : mask === HOSE_BIT.S
            ? 'N'
            : null;
  if (out) {
    // In the east-facing frame: brass at x 10..11 over the tube's rows 7..8, outlined.
    const brass = '#e0b54a';
    const dark = '#8a6418';
    const nozzle: [number, number, string][] = [
      [10, 6, ink],
      [11, 6, ink],
      [10, 7, brass],
      [11, 7, brass],
      [10, 8, dark],
      [11, 8, dark],
      [10, 9, ink],
      [11, 9, ink],
      [12, 7, ink],
      [12, 8, ink],
    ];
    for (const [x, y, c] of nozzle) {
      // Turned to face `out` (the frame is drawn east-facing, then rotated about the middle).
      const [px, py] =
        out === 'E' ? [x, y] : out === 'W' ? [15 - x, y] : out === 'S' ? [15 - y, x] : [y, 15 - x];
      put(px, py, c);
    }
  }
}

/** Overlay for a chewed hose: torn notches and a little leaking water (transparent elsewhere). */
function paintHoseChewed(put: Put): void {
  // Bite notches in the 4 px tube (pixels 6..9).
  for (const [x, y] of [
    [7, 6],
    [8, 9],
    [6, 8],
    [9, 7],
  ] as const)
    put(x, y, '#101a33');
  for (const [x, y] of [
    [11, 4],
    [12, 3],
    [4, 11],
    [3, 12],
    [12, 11],
  ] as const)
    put(x, y, '#7cc6ff');
  put(13, 2, '#cfeaff');
}

const PAINTERS: Record<TileFrameName, (put: Put) => void> = {
  hose: (put) => paintHose(put, HOSE_BIT.E | HOSE_BIT.W),
  ...(Object.fromEntries(
    Array.from({ length: 16 }, (_, m) => [`hose${m}`, (put: Put) => paintHose(put, m)]),
  ) as Record<Extract<TileFrameName, `hose${number}`>, (put: Put) => void>),
  hoseChewed: paintHoseChewed,
  dogHouse: paintDogHouse,
  sprinkler: paintSprinkler,
  sprinklerBroken: paintSprinklerBroken,
  judgesTable: paintJudgesTable,
  showLawn: paintShowLawn,
  artLight: (put) => paintArtTone(put, true),
  artDark: (put) => paintArtTone(put, false),
  pen: paintPen,
  penGate: paintPenGate,
  roboDock: paintRoboDock,
  green: paintGreen,
  greenTrampled: paintGreenTrampled,
  flag: paintFlag,
  ballWasher: paintBallWasher,
  molehill: paintMolehill,
  treeStump: paintTreeStump,
  benchSmashed: paintBenchSmashed,
  shrubFlattened: paintShrubFlattened,
  topiarySmashed: paintTopiarySmashed,
  binTipped: paintBinTipped,
  flagDown: paintFlagDown,
  cornerFlagDown: paintCornerFlagDown,
  ballWasherDown: paintBallWasherDown,
  meadowGrass: (put) => paintMeadowGrass(put),
  beehive: paintLawnBase,
  beehiveSmashed: paintBeehiveSmashed,
  sprinklerOff: paintSprinklerOff,
  path: paintGround,
  dirt: paintDirt,
  grass: (put) => paintGrass(put, 1),
  grass2: (put) => paintGrass(put, 11),
  grass3: (put) => paintGrass(put, 23),
  mowedN: (put) => paintMowed(put, Dir.N),
  mowedE: (put) => paintMowed(put, Dir.E),
  mowedS: (put) => paintMowed(put, Dir.S),
  mowedW: (put) => paintMowed(put, Dir.W),
  tallGrass: paintTallGrass,
  tallGrassCut: paintTallGrassCut,
  gnomeSmashed: paintGnomeSmashed,
  birdBathSmashed: paintBirdBathSmashed,
  sticks: paintSticks,
  flowerBed: paintFlowerBed,
  mud: paintMud,
  water: paintWater,
  rock: paintRock,
  birdBath: paintBirdBath,
  tree: paintTree,
  bigTree: paintBigTree,
  cobble: paintCobble,
  bridge: (put) => paintBridge(put),
  bridgeV: (put) => paintBridge(put, true),
  urn: paintUrn,
  urnSmashed: paintUrnSmashed,
  statue: paintStatue,
  fountain: paintFountain,
  topiary: paintTopiary,
  mazeHedge: paintMazeHedge,
  goal: paintGoal,
  cornerFlag: paintCornerFlag,
  cone: paintCone,
  coneSmashed: paintConeSmashed,
  lawnBase: paintPropBase,
  fieldLine: (put) => {
    paintGrass(put);
    for (let x = 0; x < S; x++) {
      put(x, 7, '#f4f1de');
      put(x, 8, '#f4f1de');
    }
  },
  headstone: paintHeadstone,
  lantern: paintLantern,
  thorns: paintThorns,
  deadTree: paintDeadTree,
  log: (put) => paintLog(put),
  logV: (put) => paintLog(put, true),
  logTip: (put) => paintLogTip(put, Dir.E),
  logTipW: (put) => paintLogTip(put, Dir.W),
  logTipN: (put) => paintLogTip(put, Dir.N),
  logTipS: (put) => paintLogTip(put, Dir.S),
  shrub: paintShrub,
  gnome: paintGnome,
  house: paintHouse,
  sand: paintSand,
  bench: paintBench,
  lampPost: paintLampPost,
  bin: paintBin,
  barn: paintBarn,
  fence: paintFence,
  wheat: paintWheat,
  hayBale: paintHayBale,
  crops: paintCrops,
  hedge: paintHedge,
  trough: paintTrough,
  flowerBedTrampled: paintFlowerBedTrampled,
};

const OUTLINE_RGB = [0x10, 0x18, 0x0c] as const;
const SHADOW_RGBA = [8, 16, 6, 115] as const;

/**
 * Outlines what is drawn in one frame of a canvas texture: pixels where `isObject` holds get a
 * 1 px dark ring on the pixels around them (those that are not themselves object); `shadow` also
 * casts a drop shadow 1 px right, 2 px down onto transparent pixels (sprites only; tiles paint
 * their own shadows). Runs on generated art only; PNG overrides keep their own look.
 */
function outlineFrame(
  ctx: CanvasRenderingContext2D,
  x0: number,
  w: number,
  h: number,
  isObject: (r: number, g: number, b: number, a: number) => boolean,
  shadow: boolean,
): void {
  const img = ctx.getImageData(x0, 0, w, h);
  const d = img.data;
  const at = (x: number, y: number) => (y * w + x) * 4;
  const solid = (x: number, y: number) => {
    if (x < 0 || y < 0 || x >= w || y >= h) return false;
    const i = at(x, y);
    return isObject(d[i]!, d[i + 1]!, d[i + 2]!, d[i + 3]!);
  };
  const mask = Array.from({ length: w * h }, (_, i) => solid(i % w, Math.floor(i / w)));
  const isSolid = (x: number, y: number) => x >= 0 && y >= 0 && x < w && y < h && mask[y * w + x]!;
  const out = new Uint8ClampedArray(d);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (isSolid(x, y)) continue;
      const i = at(x, y);
      let edge = false;
      for (let dy = -1; dy <= 1 && !edge; dy++) {
        for (let dx = -1; dx <= 1 && !edge; dx++) edge = isSolid(x + dx, y + dy);
      }
      if (edge) {
        out.set([...OUTLINE_RGB, 255], i);
      } else if (shadow && d[i + 3] === 0 && isSolid(x - 1, y - 2)) {
        out.set(SHADOW_RGBA, i);
      }
    }
  }
  ctx.putImageData(new ImageData(out, w, h), x0, 0);
}

const hexRgb = (hex: string): [number, number, number] => {
  const n = parseInt(hex.slice(1), 16);
  return [n >> 16, (n >> 8) & 0xff, n & 0xff];
};
/** Colors of the calm ground (and painted shadows) under obstacles: everything else is object. */
const GROUND = ['#3a9433', '#4aa53e', '#2f8530', '#163f19'].map(hexRgb);
const notGround = (r: number, g: number, b: number) =>
  !GROUND.some(([gr, gg, gb]) => gr === r && gg === g && gb === b);
/** Obstacle tiles whose object gets an outline on the ground around it. */
const OUTLINED_TILES: readonly TileFrameName[] = [
  'dogHouse',
  'sprinkler',
  'ballWasher',
  'rock',
  'birdBath',
  'tree',
  'bigTree',
  'urn',
  'statue',
  'fountain',
  'topiary',
  'cornerFlag',
  'cone',
  'headstone',
  'lantern',
  'deadTree',
  'log',
  'logV',
  'logTip',
  'shrub',
  'gnome',
  'bench',
  'lampPost',
  'bin',
  'hayBale',
  'trough',
  'sticks',
  'fence',
];

export function createTiles(scene: Phaser.Scene): void {
  const tex = scene.textures.createCanvas(TILES_KEY, S * TILE_FRAME_COUNT, S);
  if (!tex) throw new Error('Could not create tile texture');
  TILE_FRAMES.forEach((name, i) => PAINTERS[name](painter(tex.context, i * S, 0)));
  for (const name of OUTLINED_TILES) {
    outlineFrame(tex.context, TILE_FRAMES.indexOf(name) * S, S, S, notGround, false);
  }
  tex.refresh();
  // Numbered frames (like a PNG override's spritesheet) so single tiles can be drawn as images.
  for (let i = 0; i < TILE_FRAME_COUNT; i++) tex.add(i, 0, i * S, 0, S, S);
}

export function createMower(scene: Phaser.Scene, mowerId: MowerId): void {
  const key = mowerTextureKey(mowerId);
  const M = mowerFrameSize(mowerId);
  const tex = scene.textures.createCanvas(key, M * 5, M);
  if (!tex) throw new Error(`Could not create mower texture ${key}`);
  const at =
    (frame: number): Put =>
    (x, y, color) => {
      if (x < 0 || y < 0 || x >= M || y >= M) return;
      tex.context.fillStyle = color;
      tex.context.fillRect(frame * M + x, y, 1, 1);
    };
  for (const dir of [Dir.N, Dir.E, Dir.S, Dir.W]) drawMowerFrame(at(dir), mowerId, dir);
  drawMowerFrame(at(MOWER_BROKEN_FRAME), mowerId, Dir.E, true);
  tex.refresh();
  for (let i = 0; i <= MOWER_BROKEN_FRAME; i++) tex.add(i, 0, i * M, 0, M, M);
}

function paintCoin(put: Put): void {
  // A flat gold coin turned a little toward the light: its milled edge showing on the right, a
  // raised rim (lit top left, darker bottom right), the face set lower than the rim (the rim's
  // shadow along its top left, a glint along its bottom right) and the game's L stamped on it.
  ellipse(8.6, 8, 5, 6, (x, y) => put(x, y, y % 2 ? '#a86e08' : '#c98a0c'));
  ellipse(7.4, 8, 5, 6, (x, y, d, ex, ey) => {
    const lean = ex + ey;
    if (d > 0.6) put(x, y, lean < -0.2 ? '#fff3a0' : lean > 0.5 ? '#c98a0c' : '#ffd23f');
    else if (d > 0.36) put(x, y, lean < 0 ? '#d99a0c' : '#ffe27a');
    else put(x, y, '#f5c030');
  });
  // The game's mark, a bold L (as on the app icon), struck in relief: lit on its top and left
  // edges, its shadow on the face to the right and below.
  for (let y = 5; y <= 10; y++) {
    put(6, y, '#fff3a0');
    put(7, y, '#ffe27a');
    if (y <= 8) put(8, y, '#b8780a');
  }
  for (let x = 8; x <= 9; x++) {
    put(x, 9, '#fff3a0');
    put(x, 10, '#ffe27a');
  }
  for (let x = 6; x <= 10; x++) put(x, 11, '#b8780a');
  put(10, 9, '#b8780a');
  put(10, 10, '#b8780a');
  put(4, 4, '#ffffff');
}

/**
 * A secret yard's hoard: a stack of gold coins seen from the side (milled edges, lit on the left),
 * a coin face on top and one more leaning against the stack.
 */
function paintHoard(put: Put): void {
  // Four coins stacked, bottom up: each a short thick oval edge.
  for (const top of [12, 10, 8, 6]) {
    for (let x = 3; x <= 12; x++) {
      const end = x === 3 || x === 12;
      put(x, top, end ? '#c98a0c' : x <= 5 ? '#fff3a0' : '#ffd23f');
      put(x, top + 1, end ? '#a86e08' : x % 2 ? '#a86e08' : '#c98a0c');
    }
  }
  // The top coin's face.
  for (let x = 4; x <= 11; x++) put(x, 5, x <= 6 ? '#fff3a0' : '#ffe27a');
  for (let x = 5; x <= 10; x++) put(x, 4, '#ffd23f');
  // One more coin leaning on the stack, face out.
  ellipse(12.5, 11, 2.2, 3, (x, y, d) => put(x, y, d > 0.5 ? '#ffd23f' : '#c98a0c'));
  put(5, 4, '#ffffff');
}

/** A red jerry can standing on the lawn: lit top and left, a yellow spout, a white label. */
function paintGasCan(put: Put): void {
  for (let y = 5; y <= 14; y++) {
    for (let x = 4; x <= 11; x++) put(x, y, x <= 5 ? '#ff6b57' : x >= 10 ? '#a8242c' : '#d8323a');
  }
  for (let x = 4; x <= 11; x++) put(x, 5, '#ff9a84');
  for (let x = 4; x <= 11; x++) put(x, 14, '#7e1a22');
  for (let x = 6; x <= 9; x++) put(x, 3, '#3a3d42');
  put(6, 4, '#3a3d42');
  put(9, 4, '#3a3d42');
  put(10, 3, '#ffd23f');
  put(11, 2, '#ffd23f');
  put(12, 2, '#fff6b8');
  for (let y = 8; y <= 11; y++) for (let x = 6; x <= 9; x++) put(x, y, '#fffaf0');
  put(7, 9, '#d8323a');
  put(8, 10, '#d8323a');
}

/**
 * An item box (docs/DAILY_ITEMS.md): a wooden crate, its lid lit, planks across the front, and
 * its item's sign on the front in bright paint (told apart by shape and brightness, not hue).
 */
function paintCrate(put: Put): void {
  for (let y = 3; y <= 14; y++) {
    for (let x = 2; x <= 13; x++) {
      const lid = y <= 5;
      const edge = x === 2 || x === 13 || y === 14;
      put(x, y, lid ? (y === 3 ? '#f0c27a' : '#d89a4e') : edge ? '#6b3f1c' : '#a8652e');
    }
  }
  // Planks: two darker seams across the front.
  for (let x = 3; x <= 12; x++) {
    put(x, 6, '#5a3416');
    put(x, 10, '#8a4f22');
  }
}

/** Turbo's box: a yellow lightning bolt on the crate. */
function paintTurboBox(put: Put): void {
  paintCrate(put);
  const bolt = ['...##', '..##.', '.####', '..##.', '.##..', '##...'];
  bolt.forEach((row, dy) =>
    [...row].forEach(
      (ch, dx) => ch === '#' && put(5 + dx, 7 + dy, dy === 0 ? '#fff6b8' : '#ffd23f'),
    ),
  );
}

/** A trimmer cut's box: a white line spool on the crate, its line trailing out. */
function paintTrimBox(put: Put): void {
  paintCrate(put);
  const spool = ['.###.', '#####', '##.##', '#####', '.###.'];
  spool.forEach((row, dy) =>
    [...row].forEach((ch, dx) => ch === '#' && put(5 + dx, 7 + dy, '#fffaf0')),
  );
  put(10, 9, '#9be36b');
  put(11, 10, '#9be36b');
}

/** A blue toolbox with a white cross: lit lid, a handle on top, a latch. */
function paintRepair(put: Put): void {
  for (let y = 6; y <= 14; y++) {
    for (let x = 2; x <= 13; x++) put(x, y, y === 6 ? '#8ab8ff' : y <= 8 ? '#5a8de8' : '#3a63d0');
  }
  for (let x = 2; x <= 13; x++) put(x, 14, '#1f2f7a');
  for (let x = 5; x <= 10; x++) put(x, 3, '#3a3d42');
  for (const x of [5, 10]) {
    put(x, 4, '#3a3d42');
    put(x, 5, '#3a3d42');
  }
  for (let i = -2; i <= 2; i++) {
    put(8 + i, 10, '#ffffff');
    put(8, 10 + i, '#ffffff');
  }
  put(4, 8, '#ffd23f');
}

/** The diamond star's shape (15 x 14, drawn a row down in the frame). */
const DIAMOND_STAR = [
  '.......#.......',
  '.......#.......',
  '......###......',
  '......###......',
  '.....#####.....',
  '###############',
  '.#############.',
  '..###########..',
  '...#########...',
  '...#########...',
  '..#####.#####..',
  '..####...####..',
  '.####.....####.',
  '.##.........##.',
] as const;

/**
 * A rare find, and the hedge maze match's prize (owner, 2026-10-29): a diamond crystal star, each
 * arm cut into two facets lit from the top left (a darker rim round each), a brilliant white
 * heart, a glint and a touch of color in the glass, and twinkles round it.
 */
function paintRare(put: Put): void {
  const cx = 7.5;
  const cy = 7.6;
  const filled = (x: number, y: number) => DIAMOND_STAR[y]?.[x] === '#';
  const faces = ['#ffffff', '#dff8ff', '#a6e8ff', '#5fc2f2', '#3290d6'];
  const rims = ['#a6e8ff', '#5fc2f2', '#3290d6', '#2066b4', '#18478a'];
  const step = (2 * Math.PI) / 5;
  DIAMOND_STAR.forEach((row, y) =>
    [...row].forEach((ch, x) => {
      if (ch !== '#') return;
      const dx = x + 0.5 - cx;
      const dy = y + 0.5 - cy;
      // Which arm, and which of its two facets: the facet's slope to the light.
      const ang = Math.atan2(dy, dx);
      const tip = -Math.PI / 2 + Math.round((ang + Math.PI / 2) / step) * step;
      let rel = ang - tip;
      while (rel > Math.PI) rel -= 2 * Math.PI;
      while (rel < -Math.PI) rel += 2 * Math.PI;
      const n = tip + (rel < 0 ? -1.05 : 1.05);
      const lit = -0.55 * Math.cos(n) - 0.83 * Math.sin(n);
      const level = lit > 0.5 ? 1 : lit > 0 ? 2 : lit > -0.5 ? 3 : 4;
      const edge = !filled(x + 1, y) || !filled(x - 1, y) || !filled(x, y + 1) || !filled(x, y - 1);
      const heart = Math.abs(dx) < 1.1 && Math.abs(dy) < 1.3;
      put(x, y + 1, heart ? faces[0]! : edge ? rims[level]! : faces[level]!);
    }),
  );
  put(7, 3, '#ffffff');
  // A touch of color in the glass.
  put(11, 9, '#c9b8ff');
  put(4, 11, '#bff5e8');
  // Twinkles round it.
  put(1, 1, '#ffd6f5');
  put(14, 2, '#fff1a8');
  put(14, 13, '#ffffff');
}

/** A friendly dog, side on, facing right: tan coat lit along its back, floppy ear, tail up. */
function paintDog(put: Put): void {
  const coat = '#d9964f';
  const light = '#f2c085';
  const dark = '#9a5e2a';
  ellipse(7.5, 9.5, 4.6, 2.9, (x, y, d, _ex, ey) =>
    put(x, y, d > 0.75 ? dark : ey < -0.3 ? light : coat),
  );
  ellipse(12, 6.5, 2.7, 2.5, (x, y, _d, ex, ey) => put(x, y, ex + ey < -0.4 ? light : coat));
  for (const [x, y] of [
    [10, 5],
    [10, 6],
    [10, 7],
    [11, 8],
  ] as const)
    put(x, y, dark);
  put(14, 7, '#10180c');
  put(12, 6, '#10180c');
  put(13, 9, '#ff8fa8');
  for (const x of [5, 6, 9, 10]) for (let y = 12; y <= 13; y++) put(x, y, dark);
  for (const [x, y] of [
    [3, 8],
    [2, 7],
    [2, 6],
    [3, 5],
  ] as const)
    put(x, y, coat);
  for (let x = 6; x <= 9; x++) put(x, 8, '#d8323a');
}

/** A mole poking its head out of the earth: velvety gray, pink nose and paws, a lit brow. */
function paintMole(put: Put): void {
  ellipse(8, 13, 6.2, 2.6, (x, y, d, _ex, ey) =>
    put(x, y, d > 0.6 ? '#4a3220' : ey < 0 ? '#9a6b42' : '#6b4a2b'),
  );
  ellipse(8, 8.5, 3.7, 4.1, (x, y, d, ex, ey) =>
    put(x, y, ex + ey < -0.6 ? '#8a847e' : d > 0.8 ? '#3a3633' : '#5e5853'),
  );
  put(8, 9, '#ff9aa8');
  put(7, 9, '#ff9aa8');
  put(6, 7, '#10180c');
  put(9, 7, '#10180c');
  for (const x of [4, 5, 10, 11]) put(x, 11, '#ff9aa8');
}

/** A woolly sheep, side on, facing right: bright fleece puffs lit from the top left, dark face. */
function paintSheep(put: Put): void {
  const wool = '#fffaf0';
  const shade = '#d8d2c2';
  const face = '#3a3330';
  for (const x of [5, 6, 10, 11]) for (let y = 11; y <= 13; y++) put(x, y, face);
  for (const [cx, cy, r] of [
    [5, 8, 3],
    [8, 7, 3.2],
    [10, 9, 2.8],
    [7, 10, 3],
  ] as const)
    ellipse(cx, cy, r, r * 0.85, (x, y, d, ex, ey) =>
      put(x, y, d > 0.7 || ex + ey > 0.6 ? shade : wool),
    );
  ellipse(13, 7, 1.9, 2.3, (x, y) => put(x, y, face));
  put(12, 5, face);
  put(14, 6, '#ffffff');
  put(14, 8, '#7a6a64');
}

function paintSquirrel(put: Put): void {
  // Sitting up, facing right: a big bushy tail curling up behind (lit at its rim), round body,
  // head with an ear and a bright eye.
  const dark = '#8a4a1c';
  const body = '#c4782f';
  const light = '#f0b26a';
  ellipse(4.2, 6.5, 3.4, 4.8, (x, y, d, ex) => put(x, y, d > 0.65 ? dark : ex < 0 ? light : body));
  ellipse(9, 10.5, 3.4, 3, (x, y, _d, ex) => put(x, y, ex < -0.3 ? light : body));
  ellipse(11.8, 7, 2.3, 2.2, (x, y, _d, ex) => put(x, y, ex < -0.2 ? light : body));
  put(11, 4, body);
  put(11, 5, dark);
  put(13, 6, '#10180c');
  put(13, 5, '#ffffff');
  put(10, 11, '#ffe1b5');
  put(8, 13, dark);
  put(11, 13, dark);
}

function paintRabbit(put: Put): void {
  // Sitting, facing right: soft gray-white fur lit from the left, long ears, a cotton tail.
  const fur = '#eeeae2';
  const shade = '#b4ada2';
  ellipse(7, 10, 4.6, 3.3, (x, y, d, ex) => put(x, y, d > 0.7 || ex > 0.5 ? shade : fur));
  ellipse(11.6, 8, 2.5, 2.3, (x, y, _d, ex) => put(x, y, ex > 0.4 ? shade : fur));
  for (let y = 2; y <= 6; y++) {
    put(11, y, fur);
    put(13, y, shade);
  }
  put(11, 3, '#ffa8b8');
  put(11, 4, '#ffa8b8');
  put(12, 8, '#10180c');
  put(14, 9, '#ff8fa8');
  ellipse(2.5, 9.5, 1.4, 1.4, (x, y) => put(x, y, '#ffffff'));
}

/** A doe, side on, facing right: slender, tan, a white belly and tail, big ears, thin legs. */
function paintDeer(put: Put): void {
  const coat = '#c58a4e';
  const lit = '#e0aa6c';
  const leg = '#7a4f2a';
  for (const x of [4, 5, 9, 10]) for (let y = 11; y <= 14; y++) put(x, y, leg);
  ellipse(7.5, 8.5, 4.6, 2.6, (x, y, _d, _ex, ey) =>
    put(x, y, ey > 0.55 ? '#f2dcc0' : ey < -0.3 ? lit : coat),
  );
  for (let y = 4; y <= 7; y++) {
    put(10 + Math.floor((7 - y) / 2), y, coat);
    put(11 + Math.floor((7 - y) / 2), y, coat);
  }
  ellipse(13, 3.8, 2, 1.5, (x, y) => put(x, y, coat));
  put(15, 4, '#3a2a1e');
  put(13, 3, '#10180c');
  for (const [x, y, c] of [
    [11, 1, coat],
    [12, 1, coat],
    [11, 2, '#f2b8a8'],
    [2, 7, '#ffffff'],
    [3, 7, '#ffffff'],
  ] as const)
    put(x, y, c);
}

/** A fox, side on, facing right: low and orange, a bushy white-tipped tail, black socks. */
function paintFox(put: Put): void {
  const coat = '#e8742a';
  const lit = '#ff9a4a';
  for (const x of [5, 9]) for (let y = 12; y <= 13; y++) put(x, y, '#2a1e18');
  ellipse(2.6, 9.4, 2.6, 1.8, (x, y, _d, ex) => put(x, y, ex < -0.5 ? '#ffffff' : coat));
  ellipse(7, 10, 4, 2.2, (x, y, _d, _ex, ey) => put(x, y, ey < -0.3 ? lit : coat));
  ellipse(11.6, 8, 2.4, 2, (x, y, _d, _ex, ey) => put(x, y, ey < -0.3 ? lit : coat));
  for (const [x, y, c] of [
    [13, 9, '#ffffff'],
    [14, 9, '#ffffff'],
    [15, 9, '#10180c'],
    [12, 7, '#10180c'],
    [11, 10, '#ffffff'],
    [10, 5, coat],
    [10, 4, '#2a1e18'],
    [13, 5, coat],
    [13, 4, '#2a1e18'],
  ] as const)
    put(x, y, c);
}

/** A stag, side on, facing right: a big dark deer with a ruff and a crown of branching antlers. */
function paintStag(put: Put): void {
  const coat = '#8a5a32';
  const lit = '#b07a48';
  const leg = '#4a301a';
  const antler = '#ecdcbc';
  for (const x of [3, 5, 9, 11]) for (let y = 12; y <= 15; y++) put(x, y, leg);
  ellipse(7.5, 9.6, 5, 2.9, (x, y, _d, _ex, ey) =>
    put(x, y, ey > 0.6 ? '#d8b88e' : ey < -0.3 ? lit : coat),
  );
  for (let y = 5; y <= 8; y++)
    for (let x = 10; x <= 12; x++) put(x + Math.floor((8 - y) / 2), y, '#6a4424');
  ellipse(13.4, 5, 2.2, 1.6, (x, y) => put(x, y, coat));
  put(15, 5, '#2a1e18');
  put(13, 4, '#10180c');
  for (const [x, y] of [
    [12, 3],
    [11, 2],
    [10, 1],
    [10, 0],
    [12, 1],
    [11, 0],
    [14, 3],
    [14, 2],
    [15, 1],
    [15, 0],
    [13, 1],
    [13, 0],
  ] as const)
    put(x, y, antler);
  put(2, 8, '#ffffff');
}

export function createCritters(scene: Phaser.Scene): void {
  const paints = [
    paintSquirrel,
    paintRabbit,
    paintDog,
    paintSheep,
    paintMole,
    paintDeer,
    paintFox,
    paintStag,
  ];
  const tex = scene.textures.createCanvas(CRITTERS_KEY, S * paints.length, S);
  if (!tex) throw new Error('Could not create critter texture');
  paints.forEach((paint, i) => paint(painter(tex.context, S * i, 0)));
  for (let i = 0; i < paints.length; i++)
    outlineFrame(tex.context, i * S, S, S, (_r, _g, _b, a) => a > 0, true);
  tex.refresh();
  for (let i = 0; i < paints.length; i++) tex.add(i, 0, i * S, 0, S, S);
}

/**
 * The robot mower in three-quarter view from above (like the mowers): a white shell with a strip
 * of gray near face, a teal lid, wheels peeking out, and its visor with red eyes on the side it
 * heads toward (west mirrors east).
 */
function paintRobot(put: Put, dir: Direction): void {
  const shell = '#eef2f6';
  const shade = '#b8c0cc';
  // Wheels peek out at the sides.
  for (const [x, y] of [
    [2, 6],
    [13, 6],
    [2, 11],
    [13, 11],
  ] as const) {
    put(x, y, '#1f2023');
    put(x, y + 1, '#1f2023');
  }
  ellipse(7.5, 8.5, 5.6, 5.4, (x, y, d, ex, ey) =>
    put(x, y, ey > 0.62 ? '#7d8796' : ex + ey < -0.5 ? '#ffffff' : d > 0.8 ? shade : shell),
  );
  ellipse(7.5, 8, 2.8, 2.6, (x, y, d, ex, ey) =>
    put(x, y, ex + ey < -0.4 ? '#5ad0d0' : d > 0.6 ? '#1f8a8a' : '#33b5b5'),
  );
  // The visor: a dark band with red eyes, on the front.
  const eyes = (x0: number, y0: number, w: number, h: number, e: readonly [number, number][]) => {
    for (let y = y0; y < y0 + h; y++) for (let x = x0; x < x0 + w; x++) put(x, y, '#222326');
    for (const [x, y] of e) put(x, y, '#ff5a4a');
  };
  if (dir === Dir.E)
    eyes(11, 6, 2, 5, [
      [12, 7],
      [12, 9],
    ]);
  else if (dir === Dir.W)
    eyes(3, 6, 2, 5, [
      [3, 7],
      [3, 9],
    ]);
  else if (dir === Dir.S)
    eyes(5, 11, 6, 2, [
      [6, 12],
      [9, 12],
    ]);
  else {
    eyes(5, 3, 6, 2, [
      [6, 3],
      [9, 3],
    ]);
    put(7, 13, '#ffd23f');
    put(8, 13, '#ffd23f');
  }
}

export function createRival(scene: Phaser.Scene): void {
  const tex = scene.textures.createCanvas(RIVAL_KEY, S * 4, S);
  if (!tex) throw new Error('Could not create rival texture');
  for (let dir = 0; dir < 4; dir++) {
    paintRobot(painter(tex.context, dir * S, 0), dir as Direction);
    outlineFrame(tex.context, dir * S, S, S, (_r, _g, _b, a) => a > 0, true);
  }
  tex.refresh();
  for (let i = 0; i < 4; i++) tex.add(i, 0, i * S, 0, S, S);
}

export function createPickups(scene: Phaser.Scene): void {
  const paints = [
    paintCoin,
    paintGasCan,
    paintRepair,
    paintRare,
    paintHoard,
    paintTurboBox,
    paintTrimBox,
  ];
  const tex = scene.textures.createCanvas(PICKUPS_KEY, S * paints.length, S);
  if (!tex) throw new Error('Could not create pickup texture');
  paints.forEach((paint, i) => paint(painter(tex.context, i * S, 0)));
  for (let i = 0; i < paints.length; i++)
    outlineFrame(tex.context, i * S, S, S, (_r, _g, _b, a) => a > 0, true);
  tex.refresh();
  for (let i = 0; i < paints.length; i++) tex.add(i, 0, i * S, 0, S, S);
}
